import React, { useState } from 'react';
import {
  Box, Typography, Paper, Grid, Divider, 
  Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, 
  TableHead, TableRow, CircularProgress
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { GroupBudgetLine } from '../types/groupBudget';
import { EXPENSE_CATEGORIES, CATEGORY_GROUPS } from '../constants/expenseCategories';
import { getIMCCodeByCode, IMC_EXPENSE_CODES } from '../constants/imcCodes';

interface BudgetOverviewPanelProps {
  expenseLines: GroupBudgetLine[];
  getActualValueByCode: (code: string) => number;
}

interface CategorySummary {
  name: string;
  group: string;
  budgeted: number;
  actual: number;
  variance: number;
  percentageSpent: number;
  codes: string[];
}

const BudgetOverviewPanel: React.FC<BudgetOverviewPanelProps> = ({ 
  expenseLines, 
  getActualValueByCode 
}) => {
  const [expandedCategory, setExpandedCategory] = useState<string | false>(false);

  const handleChange = (category: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedCategory(isExpanded ? category : false);
  };

  // Group expenses by category
  const categorySummaries: CategorySummary[] = EXPENSE_CATEGORIES.map(category => {
    const categoryLines = expenseLines.filter(line => 
      category.relatedIMCCodes.includes(line.code)
    );
    
    const budgeted = categoryLines.reduce((sum, line) => sum + (line.totalValue || 0), 0);
    const actual = categoryLines.reduce((sum, line) => sum + getActualValueByCode(line.code), 0);
    
    return {
      name: category.name,
      group: category.group,
      budgeted,
      actual,
      variance: actual - budgeted,
      percentageSpent: budgeted > 0 ? (actual / budgeted) * 100 : 0,
      codes: categoryLines.map(line => line.code)
    };
  }).filter(summary => summary.budgeted > 0 || summary.actual > 0);

  // Group by major category
  const groupedCategories: Record<string, CategorySummary[]> = {};
  
  categorySummaries.forEach(summary => {
    if (!groupedCategories[summary.group]) {
      groupedCategories[summary.group] = [];
    }
    groupedCategories[summary.group].push(summary);
  });

  // Calculate group totals
  const groupTotals: Record<string, { budgeted: number, actual: number }> = {};
  
  Object.keys(groupedCategories).forEach(group => {
    groupTotals[group] = groupedCategories[group].reduce(
      (total, summary) => ({
        budgeted: total.budgeted + summary.budgeted,
        actual: total.actual + summary.actual
      }),
      { budgeted: 0, actual: 0 }
    );
  });

  // Get details for a specific code
  const getCodeDetails = (code: string) => {
    const line = expenseLines.find(line => line.code === code);
    const codeInfo = getIMCCodeByCode(code, IMC_EXPENSE_CODES);
    
    return {
      code,
      description: codeInfo ? codeInfo.label.replace(/^\d+ - /, '') : code,
      budgeted: line?.totalValue || 0,
      actual: getActualValueByCode(code)
    };
  };

  return (
    <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Budget Overview by Category
      </Typography>
      
      <TableContainer component={Paper} sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Category Group</TableCell>
              <TableCell align="right">Budgeted</TableCell>
              <TableCell align="right">Actual</TableCell>
              <TableCell align="right">Variance/Bal</TableCell>
              <TableCell align="right">% Spent</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.keys(groupTotals).map(group => (
              <TableRow key={group}>
                <TableCell>
                  <Typography fontWeight="bold">
                    {CATEGORY_GROUPS[group as keyof typeof CATEGORY_GROUPS]}
                  </Typography>
                </TableCell>
                <TableCell align="right">€{groupTotals[group].budgeted.toLocaleString()}</TableCell>
                <TableCell align="right">€{groupTotals[group].actual.toLocaleString()}</TableCell>
                <TableCell 
                  align="right"
                  sx={{ 
                    color: groupTotals[group].actual > groupTotals[group].budgeted 
                      ? 'error.main' 
                      : 'success.main'
                  }}
                >
                  {groupTotals[group].actual > groupTotals[group].budgeted ? '+' : ''}
                  €{(groupTotals[group].actual - groupTotals[group].budgeted).toLocaleString()}
                </TableCell>
                <TableCell align="right">
                  {groupTotals[group].budgeted > 0 
                    ? ((groupTotals[group].actual / groupTotals[group].budgeted) * 100).toFixed(1)
                    : '0'}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 2 }} />

      <Typography variant="subtitle1" gutterBottom>
        Detailed Category Breakdown
      </Typography>

      {Object.keys(groupedCategories).map(group => (
        <Box key={group} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            {CATEGORY_GROUPS[group as keyof typeof CATEGORY_GROUPS]}
          </Typography>

          {groupedCategories[group].map(category => (
            <Accordion 
              key={category.name}
              expanded={expandedCategory === category.name}
              onChange={handleChange(category.name)}
              sx={{ mb: 1 }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Grid container alignItems="center">
                  <Grid item xs={5}>
                    <Typography>{category.name}</Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2">
                      €{category.budgeted.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="body2">
                      €{category.actual.toLocaleString()}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CircularProgress 
                        variant="determinate" 
                        value={Math.min(category.percentageSpent, 100)} 
                        size={24}
                        thickness={5}
                        sx={{ 
                          mr: 1,
                          color: category.percentageSpent > 100 
                            ? 'error.main' 
                            : category.percentageSpent > 80 
                              ? 'warning.main' 
                              : 'success.main'
                        }} 
                      />
                      <Typography variant="body2">
                        {category.percentageSpent.toFixed(1)}%
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Budgeted</TableCell>
                        <TableCell align="right">Actual</TableCell>
                        <TableCell align="right">Variance/Bal</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {category.codes.map(code => {
                        const details = getCodeDetails(code);
                        return (
                          <TableRow key={code}>
                            <TableCell>{code}</TableCell>
                            <TableCell>{details.description}</TableCell>
                            <TableCell align="right">
                              €{details.budgeted.toLocaleString()}
                            </TableCell>
                            <TableCell align="right">
                              €{details.actual.toLocaleString()}
                            </TableCell>
                            <TableCell 
                              align="right"
                              sx={{ 
                                color: details.actual > details.budgeted 
                                  ? 'error.main' 
                                  : 'success.main'
                              }}
                            >
                              {details.actual > details.budgeted ? '+' : ''}
                              €{(details.actual - details.budgeted).toLocaleString()}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      ))}
    </Paper>
  );
};

export default BudgetOverviewPanel;